"use client";

import { useLocale, useTranslations } from "next-intl";
import { useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ProductCard from "@/components/main/product-card";
import FiltersBadges from "./filters-badges";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import ProductFilters from "./product-filters";

export default function ProductsCatalog({ products, categories }) {
  const t = useTranslations("productsCatalog");
  const locale = useLocale();
  const searchParams = useSearchParams();
  const [filteredProducts, setFilteredProducts] = useState(products);
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage, setProductsPerPage] = useState(20);

  useEffect(() => {
    const filters = Object.fromEntries(searchParams.entries());
    console.log(filters);

    const filtered = products.filter((product) => {
      // Search: Check both `product_name_en` and `product_name_ar`
      if (
        filters.search &&
        !(
          (product.product_name_en &&
            product.product_name_en
              .toLowerCase()
              .includes(filters.search.toLowerCase())) ||
          (product.product_name_ar &&
            product.product_name_ar
              .toLowerCase()
              .includes(filters.search.toLowerCase()))
        )
      ) {
        return false;
      }

      // inStockOnly: Check if product is in stock
      if (filters.inStockOnly === "true" && product.is_out_of_stock === 1) {
        return false;
      }

      // inPromoOnly: Check if product is on promotion
      if (filters.inPromoOnly === "true" && product.discount_type === "none") {
        return false;
      }

      // Price Filters
      if (filters.minPrice && product.price < parseInt(filters.minPrice, 10)) {
        return false;
      }

      if (filters.maxPrice && product.price > parseInt(filters.maxPrice, 10)) {
        return false;
      }

      // Categories: Check if the product category matches
      if (filters.categories) {
        const categoryIds = filters.categories.split(",").map(Number);
        if (!categoryIds.includes(product.id_category)) {
          return false;
        }
      }

      // If all checks pass, include the product
      return true;
    });

    const sorted = filtered.sort((a, b) => {
      switch (filters.sort) {
        case "price-asc":
          return a.price - b.price;
        case "price-desc":
          return b.price - a.price;
        case "best-rating":
          return b.rating - a.rating;
        case "new":
          return new Date(b.created_at) - new Date(a.created_at);
        case "a-z":
          return (
            a[`product_name_${locale}`]?.localeCompare(
              b[`product_name_${locale}`],
              locale,
            ) ||
            a.product_name?.localeCompare(b.product_name, locale) ||
            0
          );

        case "z-a":
          return (
            b[`product_name_${locale}`]?.localeCompare(
              a[`product_name_${locale}`],
              locale,
            ) ||
            b.product_name?.localeCompare(a.product_name, locale) ||
            0
          );

        default:
          return 0;
      }
    });

    setFilteredProducts(sorted);
  }, [searchParams, products, locale]);

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(
    indexOfFirstProduct,
    indexOfLastProduct,
  );

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="">
      <header className="mb-5">
        <h1 className="text-3xl font-bold">{t("ourCatalog")}</h1>
        <div className="flex flex-col justify-center gap-2.5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-medium text-gray-600">
            {t("displayingProducts", {
              filtered: filteredProducts.length,
              total: products.length,
            })}
          </p>
          <Select
            value={productsPerPage.toString()}
            onValueChange={(value) => setProductsPerPage(Number(value))}
          >
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder={t("productsPerPage")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="20">{t("perPage", { count: 20 })}</SelectItem>
              <SelectItem value="40">{t("perPage", { count: 40 })}</SelectItem>
              <SelectItem value="60">{t("perPage", { count: 60 })}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="mt-5 md:hidden">
          <ProductFilters categories={categories} />
        </div>
        <FiltersBadges categories={categories} />
      </header>

      <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
        {currentProducts.map((product) => (
          <ProductCard key={product.id_product} data={product} />
        ))}
      </div>

      <div className="flex items-center justify-center space-x-2">
        <Button
          variant="outline"
          onClick={() => paginate(1)}
          disabled={currentPage === 1}
        >
          <ChevronsLeft />
        </Button>
        <Button
          variant="outline"
          onClick={() => paginate(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ChevronLeft />
        </Button>
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          const pageNumber = currentPage - 2 + i;
          if (pageNumber > 0 && pageNumber <= totalPages) {
            return (
              <Button
                key={pageNumber}
                variant={currentPage === pageNumber ? "default" : "outline"}
                onClick={() => paginate(pageNumber)}
              >
                {pageNumber}
              </Button>
            );
          }
          return null;
        })}
        <Button
          variant="outline"
          onClick={() => paginate(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <ChevronRight />
        </Button>
        <Button
          variant="outline"
          onClick={() => paginate(totalPages)}
          disabled={currentPage === totalPages}
        >
          <ChevronsRight />
        </Button>
      </div>
    </div>
  );
}
